/**
 * API Client: centralized HTTP helper for browser-side requests
 * Shared between Electron renderer and Web packages
 */

import { PROD_API_URL, DEV_API_URL } from './constants';
import type {
  User,
  SharedTagGroup,
  SharedTag,
  SharedPost,
  Subscription,
  ShareQuota,
  SquareCommentsResponse,
  SquareCommentCountResponse,
  CreateCommentResponse,
  DeleteCommentResponse,
  CloudTag,
  CloudTagGroup,
  CloudBlogger,
  CloudPost,
  CloudImportTask,
  CloudImportItem,
  CloudImportIdMappings,
  CloudImportPreviewStats,
  CloudImportProgress,
  CloudImportConflictStrategy,
  CloudImportUploadMode,
  CloudImportItemType,
  CreateImportTaskResponse,
  GetImportStatusResponse,
  BatchImportItemsResponse,
  UploadMediaResponse,
} from './types';

// === API Response Types ===

export interface ShareTagGroupResponse {
  message: string;
  sharedTagGroup: SharedTagGroup;
}

export interface ShareTagResponse {
  success: boolean;
  message: string;
  sharedTag: SharedTag;
}

export interface IncrementalUpdateResponse {
  message: string;
  sharedTagGroup: SharedTagGroup;
  stats: {
    imagesDeleted: number;
    imagesAdded: number;
    totalImages: number;
  };
}

export interface IncrementalUpdateTagResponse {
  message: string;
  success: boolean;
  sharedTag: SharedTag;
  stats?: {
    imagesDeleted: number;
    imagesAdded: number;
    totalImages: number;
  };
}

export interface PublicTagGroupDetailResponse {
  tagGroup: SharedTagGroup;
}

export interface PublicTagDetailResponse {
  tag: SharedTag;
}

export interface MySubscriptionsResponse {
  subscriptions: Subscription[];
}

export interface HardDeleteResponse {
  message: string;
  deletedSubscriptions?: number;
}

// Detect environment - works in both Node.js and browser
function isDevelopment(): boolean {
  // Check Node.js environment first
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV === 'development';
  }
  // Check for Vite dev mode via URL (localhost)
  if (typeof window !== 'undefined' && window.location) {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }
  // Default to production
  return false;
}

const API_URL = isDevelopment() ? DEV_API_URL : PROD_API_URL;

// In-memory auth token cache
let accessToken: string | null = null;
let refreshToken: string | null = null;

// Token persistence callbacks (to be set by the consuming app)
let onTokensChange: ((tokens: { accessToken: string | null; refreshToken: string | null }) => void) | null = null;

// Refresh state to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Token refresh retry configuration
const REFRESH_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Check if an error is a network-related error (timeout, connection failure, etc.)
 * These errors should NOT cause token clearing
 */
function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === 'fetch failed') {
    return true;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('timeout') ||
      msg.includes('network') ||
      msg.includes('connection') ||
      msg.includes('econnrefused') ||
      msg.includes('enotfound') ||
      msg.includes('fetch failed')
    );
  }
  return false;
}

/**
 * Check if an HTTP status code indicates an auth error (token invalid/expired)
 * Only these should cause token clearing
 */
function isAuthError(status: number): boolean {
  return status === 401 || status === 403;
}

/**
 * Delay helper with exponential backoff
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Set callback for token changes (for persistence)
 */
export function setTokenChangeCallback(callback: typeof onTokensChange) {
  onTokensChange = callback;
}

/**
 * Update the cached auth tokens
 */
export function setAuthTokens(tokens: { accessToken?: string | null; refreshToken?: string | null } = {}) {
  accessToken = tokens.accessToken ?? null;
  refreshToken = tokens.refreshToken ?? null;
  onTokensChange?.({ accessToken, refreshToken });
}

/**
 * Clear cached auth tokens
 */
export function clearAuthTokens() {
  accessToken = null;
  refreshToken = null;
  onTokensChange?.({ accessToken: null, refreshToken: null });
}

/**
 * Get current auth tokens
 */
export function getAuthTokens() {
  return { accessToken, refreshToken };
}

/**
 * Initialize tokens from storage (call on app startup)
 */
export function initAuthTokens(tokens: { accessToken?: string | null; refreshToken?: string | null }) {
  accessToken = tokens.accessToken ?? null;
  refreshToken = tokens.refreshToken ?? null;
}

function buildHeaders(customHeaders: Record<string, string> = {}): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
}

interface ApiError extends Error {
  code?: string;
  status?: number;
  details?: unknown;
}

interface ApiFetchOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: unknown;
  skipAuthRefresh?: boolean; // Internal flag to prevent infinite refresh loops
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

/**
 * Result of a token refresh attempt
 */
interface RefreshResult {
  success: boolean;
  isNetworkError?: boolean;
  shouldClearTokens?: boolean;
}

/**
 * Single attempt to refresh token (internal helper)
 */
async function attemptRefresh(): Promise<RefreshResult> {
  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      const isAuth = isAuthError(response.status);
      console.error('[apiClient] Token refresh failed:', response.status, isAuth ? '(auth error)' : '(server error)');

      // Only clear tokens for auth errors (401/403), not server errors (500)
      return {
        success: false,
        isNetworkError: false,
        shouldClearTokens: isAuth,
      };
    }

    const data = await response.json() as RefreshResponse;

    setAuthTokens({
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
    });

    console.log('[apiClient] Token refreshed successfully');
    return { success: true };
  } catch (error) {
    const isNetwork = isNetworkError(error);
    console.error('[apiClient] Token refresh error:', error, isNetwork ? '(network error, keeping tokens)' : '');

    // Network errors should NOT clear tokens - the token might still be valid
    return {
      success: false,
      isNetworkError: isNetwork,
      shouldClearTokens: !isNetwork, // Only clear for non-network errors
    };
  }
}

/**
 * Attempt to refresh the access token using the refresh token
 * - Implements retry logic with exponential backoff for network errors
 * - Only clears tokens for auth errors (401/403), not network errors
 * Returns true if successful, false otherwise
 */
async function tryRefreshToken(): Promise<boolean> {
  // If already refreshing, wait for the existing refresh to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  if (!refreshToken) {
    console.log('[apiClient] No refresh token available');
    return false;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    let lastResult: RefreshResult = { success: false };

    for (let attempt = 0; attempt <= REFRESH_RETRY_CONFIG.maxRetries; attempt++) {
      if (attempt > 0) {
        // Exponential backoff with jitter
        const delayMs = Math.min(
          REFRESH_RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt - 1) + Math.random() * 500,
          REFRESH_RETRY_CONFIG.maxDelayMs
        );
        console.log(`[apiClient] Retry ${attempt}/${REFRESH_RETRY_CONFIG.maxRetries} after ${Math.round(delayMs)}ms...`);
        await delay(delayMs);
      } else {
        console.log('[apiClient] Attempting to refresh token...');
      }

      lastResult = await attemptRefresh();

      if (lastResult.success) {
        return true;
      }

      // Don't retry for auth errors (token is definitely invalid)
      if (lastResult.shouldClearTokens) {
        console.log('[apiClient] Auth error detected, clearing tokens');
        clearAuthTokens();
        return false;
      }

      // Don't retry for non-network errors that aren't auth errors (unexpected)
      if (!lastResult.isNetworkError) {
        console.log('[apiClient] Non-retryable error, but keeping tokens');
        return false;
      }
    }

    // All retries exhausted due to network errors
    // Keep tokens - user might come back online later
    console.log('[apiClient] All refresh retries exhausted (network issues), keeping tokens for later retry');
    return false;
  })();

  try {
    return await refreshPromise;
  } finally {
    isRefreshing = false;
    refreshPromise = null;
  }
}

/**
 * Generic fetch wrapper with automatic token refresh
 */
async function apiFetch<T = unknown>(
  endpoint: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const url = `${API_URL}${endpoint}`;
  const config: RequestInit = {
    method: options.method,
    headers: buildHeaders(options.headers),
  };

  if (options.body !== undefined) {
    config.body = typeof options.body === 'string' ? options.body : JSON.stringify(options.body);
  }

  let response = await fetch(url, config);
  
  // If 401 and we haven't tried refreshing yet, attempt to refresh and retry
  if (response.status === 401 && !options.skipAuthRefresh && refreshToken) {
    console.log('[apiClient] Got 401, attempting token refresh...');
    
    const refreshed = await tryRefreshToken();
    
    if (refreshed) {
      // Retry the original request with new token
      console.log('[apiClient] Retrying request with new token...');
      config.headers = buildHeaders(options.headers);
      response = await fetch(url, config);
    }
  }

  const text = await response.text();
  let payload: T | null = null;

  if (text) {
    try {
      payload = JSON.parse(text) as T;
    } catch {
      payload = text as unknown as T;
    }
  }

  if (!response.ok) {
    console.error('[apiClient] Request failed:', {
      url,
      status: response.status,
      payload,
    });
    const error: ApiError = new Error(
      (payload as { message?: string })?.message ||
        `API request failed with status ${response.status} (${response.statusText})`
    );
    error.code = (payload as { code?: string })?.code;
    error.status = response.status;
    error.details = (payload as { details?: unknown })?.details;
    throw error;
  }

  return payload as T;
}

// === Auth API ===

interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export async function registerUser(data: { email: string; password: string; username: string }) {
  return apiFetch<{ message: string }>('/auth/register', {
    method: 'POST',
    body: data,
  });
}

export async function loginUser(data: { email: string; password: string }) {
  const result = await apiFetch<AuthResponse>('/auth/login', {
    method: 'POST',
    body: data,
  });

  setAuthTokens({
    accessToken: result.accessToken,
    refreshToken: result.refreshToken,
  });

  return result;
}

export async function resendVerificationEmail(data: { email: string }) {
  return apiFetch<{ message: string }>('/auth/resend-confirmation', {
    method: 'POST',
    body: data,
  });
}

export async function logoutUser(tokens?: { accessToken?: string | null; refreshToken?: string | null }) {
  const tokenPayload = {
    accessToken: tokens?.accessToken ?? accessToken,
    refreshToken: tokens?.refreshToken ?? refreshToken,
  };

  if (!tokenPayload.accessToken && !tokenPayload.refreshToken) {
    clearAuthTokens();
    return { message: 'No auth tokens present, cleared local session.' };
  }

  const result = await apiFetch<{ message: string }>('/auth/logout', {
    method: 'POST',
    body: tokenPayload,
  });

  clearAuthTokens();
  return result;
}

export async function fetchUserProfile() {
  return apiFetch<{ user: User }>('/auth/profile', {
    method: 'GET',
  });
}

export async function updateUserProfile(data: { username?: string; avatarUrl?: string }) {
  return apiFetch<{ user: User }>('/auth/profile', {
    method: 'PATCH',
    body: data,
  });
}

export async function uploadAvatar(avatarData: string) {
  return apiFetch<{ avatarUrl: string }>('/auth/upload-avatar', {
    method: 'POST',
    body: { avatar: avatarData },
  });
}

// === Tag Group 分享 API ===

export async function getShareQuota() {
  return apiFetch<ShareQuota>('/tag-groups/share-quota', {
    method: 'GET',
  });
}

export async function shareTagGroup(data: {
  localTagGroupId: string;
  name: string;
  description?: string;
  posts: SharedPost[];
  postCount: number;
  imageCount: number;
  tags?: string[];
  isAnonymous?: boolean;
}) {
  return apiFetch<ShareTagGroupResponse>('/tag-groups/share', {
    method: 'POST',
    body: data,
  });
}

export async function updateSharedTagGroup(
  sharedTagGroupId: string,
  data: { name?: string; description?: string; images?: string[]; postCount?: number; tags?: string[] }
) {
  return apiFetch<SharedTagGroup>(`/tag-groups/${sharedTagGroupId}/update-share`, {
    method: 'PUT',
    body: data,
  });
}

export async function incrementalUpdateSharedTagGroup(
  sharedTagGroupId: string,
  data: {
    name: string;
    description?: string;
    posts: SharedPost[];
    postCount: number;
    imageCount: number;
    tags?: string[];
  }
) {
  return apiFetch<IncrementalUpdateResponse>(`/tag-groups/${sharedTagGroupId}/incremental-update`, {
    method: 'PATCH',
    body: data,
  });
}

export async function unshareTagGroup(sharedTagGroupId: string) {
  return apiFetch<{ message: string }>(`/tag-groups/${sharedTagGroupId}/unshare`, {
    method: 'DELETE',
  });
}

export async function hardDeleteSharedTagGroup(sharedTagGroupId: string) {
  return apiFetch<HardDeleteResponse>(`/tag-groups/${sharedTagGroupId}/hard-delete`, {
    method: 'DELETE',
  });
}

export async function getMySharedTagGroups() {
  return apiFetch<SharedTagGroup[]>('/tag-groups/my-shared', {
    method: 'GET',
  });
}

// === 独立 Tag 分享 API ===

export async function shareTag(data: {
  localTagId: string;
  name: string;
  description?: string;
  posts: SharedPost[];
  postCount: number;
  imageCount: number;
  isAnonymous?: boolean;
}) {
  return apiFetch<ShareTagResponse>('/tags/share', {
    method: 'POST',
    body: data,
  });
}

export async function updateSharedTag(
  sharedTagId: string,
  data: { name?: string; description?: string; images?: string[]; postCount?: number }
) {
  return apiFetch<SharedTag>(`/tags/${sharedTagId}/update-share`, {
    method: 'PUT',
    body: data,
  });
}

export async function incrementalUpdateSharedTag(
  sharedTagId: string,
  data: { name: string; description?: string; posts: SharedPost[]; postCount: number }
) {
  return apiFetch<IncrementalUpdateTagResponse>(`/tags/${sharedTagId}/incremental-update`, {
    method: 'PATCH',
    body: data,
  });
}

export async function unshareTag(sharedTagId: string) {
  return apiFetch<{ message: string }>(`/tags/${sharedTagId}/unshare`, {
    method: 'DELETE',
  });
}

// === 广场 API ===

interface PaginatedTagGroupsResponse {
  tagGroups: SharedTagGroup[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface PaginatedTagsResponse {
  sharedTags?: SharedTag[];
  tags?: SharedTag[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getPublicTags(options: { page?: number; limit?: number; sortBy?: 'latest' | 'hot' } = {}) {
  const { page = 1, limit = 20, sortBy = 'latest' } = options;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
  });

  return apiFetch<PaginatedTagsResponse>(`/tags/public?${params.toString()}`, {
    method: 'GET',
  });
}

export async function getPublicTagGroups(options: { page?: number; limit?: number; sortBy?: 'latest' | 'hot' } = {}) {
  const { page = 1, limit = 20, sortBy = 'latest' } = options;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
  });

  return apiFetch<PaginatedTagGroupsResponse>(`/tag-groups/public?${params.toString()}`, {
    method: 'GET',
  });
}

export async function getPublicTagGroupDetail(sharedTagGroupId: string) {
  return apiFetch<PublicTagGroupDetailResponse>(`/tag-groups/public/${sharedTagGroupId}`, {
    method: 'GET',
  });
}

export async function getPublicTagDetail(sharedTagId: string) {
  return apiFetch<PublicTagDetailResponse>(`/tags/public/${sharedTagId}`, {
    method: 'GET',
  });
}

// === 订阅 API ===

export async function subscribeTagGroup(sharedTagGroupId: string) {
  return apiFetch<Subscription>('/subscriptions', {
    method: 'POST',
    body: { tagGroupId: sharedTagGroupId },
  });
}

export async function subscribeCloudTagGroup(cloudTagGroupId: string) {
  return apiFetch<Subscription>('/subscriptions', {
    method: 'POST',
    body: { cloudTagGroupId },
  });
}

export async function unsubscribeTagGroup(sharedTagGroupId: string) {
  return apiFetch<{ message: string }>(`/subscriptions/${sharedTagGroupId}`, {
    method: 'DELETE',
  });
}

export async function subscribeTag(sharedTagId: string) {
  return apiFetch<{ message: string }>(`/tags/${sharedTagId}/subscribe`, {
    method: 'POST',
  });
}

export async function unsubscribeTag(sharedTagId: string) {
  return apiFetch<{ message: string }>(`/tags/${sharedTagId}/unsubscribe`, {
    method: 'DELETE',
  });
}

export async function getMySubscriptions() {
  return apiFetch<MySubscriptionsResponse>('/subscriptions/my', {
    method: 'GET',
  });
}

export async function checkSubscriptionStatus(sharedTagGroupId: string) {
  return apiFetch<{ isSubscribed: boolean }>(`/subscriptions/check/${sharedTagGroupId}`, {
    method: 'GET',
  });
}

export async function markSubscriptionAsViewed(sharedTagGroupId: string) {
  return apiFetch<{ message: string }>(`/subscriptions/${sharedTagGroupId}/viewed`, {
    method: 'PATCH',
  });
}

// === 图片批量获取 API ===

export async function getTagGroupsTopImages(ids: string[]) {
  return apiFetch<Record<string, string[]>>('/tag-groups/top-images', {
    method: 'POST',
    body: { ids },
  });
}

export async function getTagsTopImages(ids: string[]) {
  return apiFetch<Record<string, string[]>>('/tags/top-images', {
    method: 'POST',
    body: { ids },
  });
}

// === 评论 API ===

/**
 * 创建评论
 */
export async function createSquareComment(data: {
  cloudPostId: string;
  content: string;
}) {
  return apiFetch<CreateCommentResponse>('/comments', {
    method: 'POST',
    body: data,
  });
}

/**
 * 获取帖子评论列表（游标懒加载）
 */
export async function getSquareComments(
  postId: string,
  options: { cursor?: string; limit?: number } = {}
) {
  const { cursor, limit = 20 } = options;
  const params = new URLSearchParams({ limit: limit.toString() });
  if (cursor) params.set('cursor', cursor);

  return apiFetch<SquareCommentsResponse>(
    `/comments/${postId}?${params.toString()}`,
    { method: 'GET' }
  );
}

/**
 * 获取帖子评论数量
 */
export async function getSquareCommentCount(postId: string) {
  return apiFetch<SquareCommentCountResponse>(
    `/comments/${postId}/count`,
    { method: 'GET' }
  );
}

/**
 * 删除评论（仅限自己的评论）
 */
export async function deleteSquareComment(commentId: string) {
  return apiFetch<DeleteCommentResponse>(`/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// === 通知相关（预留）===

/**
 * 获取未读评论
 */
export async function getUnreadComments(limit = 20) {
  return apiFetch<SquareCommentsResponse>(
    `/comments/unread?limit=${limit}`,
    { method: 'GET' }
  );
}

/**
 * 标记评论为已读
 */
export async function markCommentAsRead(commentId: string) {
  return apiFetch<{ message: string }>(
    `/comments/${commentId}/read`,
    { method: 'PATCH' }
  );
}

/**
 * 获取未读评论数量
 */
export async function getUnreadCommentCount() {
  return apiFetch<{ count: number }>('/comments/unread/count', {
    method: 'GET',
  });
}

/**
 * 标记所有评论为已读
 */
export async function markAllCommentsAsRead() {
  return apiFetch<{ message: string; count: number }>('/comments/read-all', {
    method: 'PATCH',
  });
}

// === 云剪藏簿 API (Cloud Clippings) ===

// -- Cloud Tags --

export async function getCloudTags() {
  return apiFetch<{ tags: CloudTag[] }>('/cloud-clippings/tags', {
    method: 'GET',
  });
}

export async function createCloudTag(data: { name: string; groupId?: string }) {
  return apiFetch<{ tag: CloudTag }>('/cloud-clippings/tags', {
    method: 'POST',
    body: data,
  });
}

export async function updateCloudTag(id: string, data: { name?: string; description?: string }) {
  return apiFetch<{ tag: CloudTag }>(`/cloud-clippings/tags/${id}`, {
    method: 'PUT',
    body: data,
  });
}

export async function deleteCloudTag(id: string) {
  return apiFetch<{ message: string }>(`/cloud-clippings/tags/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderCloudTags(
  tagOrders: Array<{ id: string; order: number; groupId?: string | null }>
) {
  return apiFetch<{ message: string }>('/cloud-clippings/tags/reorder', {
    method: 'PUT',
    body: { tagOrders },
  });
}

// -- Cloud Tag Groups --

export async function getCloudTagGroups() {
  return apiFetch<{ tagGroups: CloudTagGroup[] }>('/cloud-clippings/tag-groups', {
    method: 'GET',
  });
}

export async function createCloudTagGroup(data: { name: string }) {
  return apiFetch<{ tagGroup: CloudTagGroup }>('/cloud-clippings/tag-groups', {
    method: 'POST',
    body: data,
  });
}

export async function updateCloudTagGroup(
  id: string,
  data: { name?: string; description?: string; isExpanded?: boolean }
) {
  return apiFetch<{ tagGroup: CloudTagGroup }>(`/cloud-clippings/tag-groups/${id}`, {
    method: 'PUT',
    body: data,
  });
}

export async function deleteCloudTagGroup(id: string) {
  return apiFetch<{ message: string }>(`/cloud-clippings/tag-groups/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderCloudTagGroups(
  groupOrders: Array<{ id: string; order: number }>
) {
  return apiFetch<{ message: string }>('/cloud-clippings/tag-groups/reorder', {
    method: 'PUT',
    body: { groupOrders },
  });
}

// -- Cloud Bloggers --

export async function getCloudBloggers(params?: { page?: number; limit?: number; all?: boolean }) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.all) queryParams.set('all', 'true');

  const queryString = queryParams.toString();
  return apiFetch<{
    bloggers: CloudBlogger[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  }>(
    `/cloud-clippings/bloggers${queryString ? `?${queryString}` : ''}`,
    { method: 'GET' }
  );
}

export async function deleteCloudBlogger(id: string) {
  return apiFetch<{ message: string }>(`/cloud-clippings/bloggers/${id}`, {
    method: 'DELETE',
  });
}

export async function updateCloudBloggerTags(id: string, tagIds: string[]) {
  return apiFetch<{ blogger: CloudBlogger }>(`/cloud-clippings/bloggers/${id}/tags`, {
    method: 'PUT',
    body: { tagIds },
  });
}

export async function updateCloudBlogger(id: string, data: { annotation?: string }) {
  return apiFetch<{ blogger: CloudBlogger }>(`/cloud-clippings/bloggers/${id}`, {
    method: 'PUT',
    body: data,
  });
}

// -- Cloud Posts --

export async function getCloudPosts(params?: {
  tagId?: string;
  tagGroupId?: string;
  bloggerId?: string;
  isStarred?: boolean;
  isVideo?: boolean;
  isPageClip?: boolean;
  noTags?: boolean;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  if (params?.tagId) queryParams.set('tagId', params.tagId);
  if (params?.tagGroupId) queryParams.set('tagGroupId', params.tagGroupId);
  if (params?.bloggerId) queryParams.set('bloggerId', params.bloggerId);
  if (params?.isStarred) queryParams.set('isStarred', 'true');
  if (params?.isVideo) queryParams.set('isVideo', 'true');
  if (params?.isPageClip) queryParams.set('isPageClip', 'true');
  if (params?.noTags) queryParams.set('noTags', 'true');
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());

  const queryString = queryParams.toString();
  return apiFetch<{ posts: CloudPost[]; pagination?: any }>(
    `/cloud-clippings/posts${queryString ? `?${queryString}` : ''}`,
    { method: 'GET' }
  );
}

export async function deleteCloudPost(id: string) {
  return apiFetch<{ message: string }>(`/cloud-clippings/posts/${id}`, {
    method: 'DELETE',
  });
}

export async function toggleCloudPostStar(id: string) {
  return apiFetch<{ post: CloudPost }>(`/cloud-clippings/posts/${id}/star`, {
    method: 'PATCH',
  });
}

export async function toggleCloudPostPin(id: string) {
  return apiFetch<{ post: CloudPost }>(`/cloud-clippings/posts/${id}/pin`, {
    method: 'PATCH',
  });
}

export async function updateCloudPostTags(id: string, tagIds: string[]) {
  return apiFetch<{ post: CloudPost }>(`/cloud-clippings/posts/${id}`, {
    method: 'PUT',
    body: { tagIds },
  });
}

export async function deleteCloudPosts(ids: string[]) {
  return apiFetch<{ message: string; deletedCount: number }>('/cloud-clippings/posts/batch', {
    method: 'DELETE',
    body: { postIds: ids },
  });
}

// -- Cloud Tag/TagGroup Share --

export interface CloudShareParams {
  isPublic: boolean;
  alias?: string;
  description?: string;
  isAnonymous?: boolean;
  isEncrypted?: boolean;
  accessPassword?: string;
}

export interface CloudShareResponse {
  success: boolean;
  tag?: CloudTag;
  tagGroup?: CloudTagGroup;
  action: 'shared' | 'updated' | 'unshared';
}

/**
 * 分享/更新分享/取消分享 CloudTag
 * PATCH /api/cloud-clippings/tags/:id/share
 */
export async function shareCloudTag(id: string, params: CloudShareParams) {
  return apiFetch<CloudShareResponse>(`/cloud-clippings/tags/${id}/share`, {
    method: 'PATCH',
    body: params,
  });
}

/**
 * 分享/更新分享/取消分享 CloudTagGroup
 * PATCH /api/cloud-clippings/tag-groups/:id/share
 */
export async function shareCloudTagGroup(id: string, params: CloudShareParams) {
  return apiFetch<CloudShareResponse>(`/cloud-clippings/tag-groups/${id}/share`, {
    method: 'PATCH',
    body: params,
  });
}

// -- Cloud Public API --

export interface CloudPublicTagGroup {
  id: string;
  name: string;
  description: string | null;
  isAnonymous: boolean;
  isEncrypted: boolean;
  sharedAt: string;
  subscriberCount: number;
  postCount: number;
  tags: string[];
  topImages?: string[];
  uploader: {
    id: string;
    username: string;
    userGroup: string;
  };
}

export interface CloudPublicTag {
  id: string;
  name: string;
  description: string | null;
  isAnonymous: boolean;
  isEncrypted: boolean;
  sharedAt: string;
  subscriberCount: number;
  postCount: number;
  topImages?: string[];
  uploader: {
    id: string;
    username: string;
    userGroup: string;
  };
}

export interface CloudPublicTagGroupsResponse {
  tagGroups: CloudPublicTagGroup[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CloudPublicTagsResponse {
  tags: CloudPublicTag[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CloudPublicTagGroupDetailResponse {
  tagGroup: CloudPublicTagGroup;
  posts: CloudPost[];
  tags: string[];
  needsPassword: boolean;
}

export interface CloudPublicTagDetailResponse {
  tag: CloudPublicTag;
  posts: CloudPost[];
  needsPassword: boolean;
}

export interface CloudPublicTopImagesResponse {
  topImages: Record<string, string[]>;
  topBloggers: Record<string, Array<{ nickname: string; avatarUrl: string | null }>>;
}

/**
 * 获取公开的 CloudTagGroup 列表
 * GET /api/cloud-clippings/public/tag-groups
 */
export async function getCloudPublicTagGroups(options: { page?: number; limit?: number; sortBy?: 'latest' | 'hot' } = {}) {
  const { page = 1, limit = 20, sortBy = 'latest' } = options;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
  });

  return apiFetch<CloudPublicTagGroupsResponse>(`/cloud-clippings/public/tag-groups?${params.toString()}`, {
    method: 'GET',
  });
}

/**
 * 获取公开的 CloudTag 列表
 * GET /api/cloud-clippings/public/tags
 */
export async function getCloudPublicTags(options: { page?: number; limit?: number; sortBy?: 'latest' | 'hot' } = {}) {
  const { page = 1, limit = 20, sortBy = 'latest' } = options;
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    sortBy,
  });

  return apiFetch<CloudPublicTagsResponse>(`/cloud-clippings/public/tags?${params.toString()}`, {
    method: 'GET',
  });
}

/**
 * 获取公开的 CloudTagGroup 详情
 * GET /api/cloud-clippings/public/tag-groups/:id
 */
export async function getCloudPublicTagGroupDetail(id: string, params?: { page?: number; limit?: number }) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  const query = queryParams.toString();
  return apiFetch<CloudPublicTagGroupDetailResponse>(`/cloud-clippings/public/tag-groups/${id}${query ? `?${query}` : ''}`, {
    method: 'GET',
  });
}

/**
 * 获取公开的 CloudTag 详情
 * GET /api/cloud-clippings/public/tags/:id
 */
export async function getCloudPublicTagDetail(id: string, params?: { page?: number; limit?: number }) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  const query = queryParams.toString();
  return apiFetch<CloudPublicTagDetailResponse>(`/cloud-clippings/public/tags/${id}${query ? `?${query}` : ''}`, {
    method: 'GET',
  });
}

/**
 * 批量获取 topImages 和 topBloggers
 * POST /api/cloud-clippings/public/top-images
 */
export async function getCloudPublicTopImages(data: { tagGroupIds?: string[]; tagIds?: string[] }) {
  return apiFetch<CloudPublicTopImagesResponse>('/cloud-clippings/public/top-images', {
    method: 'POST',
    body: data,
  });
}

/**
 * 验证加密标签组的密码
 * POST /api/cloud-clippings/public/tag-groups/:id/verify
 */
export async function verifyCloudTagGroupPassword(id: string, password: string) {
  return apiFetch<CloudPublicTagGroupDetailResponse & { valid: boolean }>(`/cloud-clippings/public/tag-groups/${id}/verify`, {
    method: 'POST',
    body: { password },
  });
}

/**
 * 验证加密标签的密码
 * POST /api/cloud-clippings/public/tags/:id/verify
 */
export async function verifyCloudTagPassword(id: string, password: string) {
  return apiFetch<CloudPublicTagDetailResponse & { valid: boolean }>(`/cloud-clippings/public/tags/${id}/verify`, {
    method: 'POST',
    body: { password },
  });
}

/**
 * Create a Cloud Clippings API client instance
 * Used by the useMyCloudClippingsPage hook
 */
export function createCloudClippingsApiClient() {
  return {
    // Tags
    getTags: getCloudTags,
    createTag: createCloudTag,
    updateTag: updateCloudTag,
    deleteTag: deleteCloudTag,
    reorderTags: reorderCloudTags,
    shareTag: shareCloudTag,

    // Tag Groups
    getTagGroups: getCloudTagGroups,
    createTagGroup: createCloudTagGroup,
    updateTagGroup: updateCloudTagGroup,
    deleteTagGroup: deleteCloudTagGroup,
    reorderTagGroups: reorderCloudTagGroups,
    shareTagGroup: shareCloudTagGroup,

    // Bloggers
    getBloggers: getCloudBloggers,
    deleteBlogger: deleteCloudBlogger,
    updateBloggerTags: updateCloudBloggerTags,
    updateBlogger: updateCloudBlogger,

    // Posts
    getPosts: getCloudPosts,
    deletePost: deleteCloudPost,
    deletePosts: deleteCloudPosts,
    toggleStarPost: toggleCloudPostStar,
    togglePinPost: toggleCloudPostPin,
    updatePostTags: updateCloudPostTags,

    // Public
    getPublicTagGroups: getCloudPublicTagGroups,
    getPublicTags: getCloudPublicTags,
    getPublicTagGroupDetail: getCloudPublicTagGroupDetail,
    getPublicTagDetail: getCloudPublicTagDetail,
    getPublicTopImages: getCloudPublicTopImages,
  };
}

// === 云端导入 API (Cloud Import) ===

// 注意：CreateImportTaskResponse, GetImportStatusResponse, BatchImportItemsResponse,
// UploadMediaResponse 已在 types.ts 中定义，这里直接使用

/**
 * 创建导入任务
 * POST /api/cloud-clippings/import/create-task
 */
export async function createImportTask(data: {
  selectedTagIds: string[];
  selectedTagGroupIds: string[];
  conflictStrategy: CloudImportConflictStrategy;
  preview: {
    tagGroups: any[];
    tags: any[];
    bloggers: any[];
    posts: any[];
  };
}) {
  return apiFetch<CreateImportTaskResponse>('/cloud-clippings/import/create-task', {
    method: 'POST',
    body: data,
  });
}

/**
 * 开始导入任务
 * POST /api/cloud-clippings/import/:taskId/start
 */
export async function startImportTask(taskId: string) {
  return apiFetch<{ success: boolean; message: string }>(
    `/cloud-clippings/import/${taskId}/start`,
    { method: 'POST' }
  );
}

/**
 * 暂停导入任务
 * POST /api/cloud-clippings/import/:taskId/pause
 */
export async function pauseImportTask(taskId: string) {
  return apiFetch<{ success: boolean; message: string }>(
    `/cloud-clippings/import/${taskId}/pause`,
    { method: 'POST' }
  );
}

/**
 * 恢复导入任务
 * POST /api/cloud-clippings/import/:taskId/resume
 */
export async function resumeImportTask(taskId: string) {
  return apiFetch<{ success: boolean; message: string }>(
    `/cloud-clippings/import/${taskId}/resume`,
    { method: 'POST' }
  );
}

/**
 * 取消导入任务
 * POST /api/cloud-clippings/import/:taskId/cancel
 */
export async function cancelImportTask(taskId: string) {
  return apiFetch<{ success: boolean; message: string }>(
    `/cloud-clippings/import/${taskId}/cancel`,
    { method: 'POST' }
  );
}

/**
 * 获取导入任务状态
 * GET /api/cloud-clippings/import/:taskId/status
 */
export async function getImportTaskStatus(taskId: string) {
  return apiFetch<GetImportStatusResponse>(
    `/cloud-clippings/import/${taskId}/status`,
    { method: 'GET' }
  );
}

/**
 * 批量导入数据项
 * POST /api/cloud-clippings/import/:taskId/batch-items
 */
export async function batchImportItems(
  taskId: string,
  data: {
    items: Array<{ type: CloudImportItemType; data: any }>;
    idMappings?: CloudImportIdMappings;
  }
) {
  return apiFetch<BatchImportItemsResponse>(
    `/cloud-clippings/import/${taskId}/batch-items`,
    { method: 'POST', body: data }
  );
}

/**
 * 上传媒体文件
 * POST /api/cloud-clippings/import/:taskId/upload-media
 */
export async function uploadImportMedia(
  taskId: string,
  data: {
    postId: string;
    images?: string[];
    video?: string;
  }
) {
  return apiFetch<UploadMediaResponse>(
    `/cloud-clippings/import/${taskId}/upload-media`,
    { method: 'POST', body: data }
  );
}

/**
 * 获取用户的所有导入任务
 * GET /api/cloud-clippings/import/tasks
 */
export async function getImportTasks() {
  return apiFetch<{ success: boolean; tasks: CloudImportTask[] }>(
    '/cloud-clippings/import/tasks',
    { method: 'GET' }
  );
}

/**
 * 获取待处理的导入项（用于断点续传）
 * GET /api/cloud-clippings/import/:taskId/pending-items
 */
export async function getPendingImportItems(
  taskId: string,
  options?: { type?: CloudImportItemType; limit?: number }
) {
  const params = new URLSearchParams();
  if (options?.type) params.set('type', options.type);
  if (options?.limit) params.set('limit', options.limit.toString());

  const queryString = params.toString();
  return apiFetch<{ success: boolean; items: CloudImportItem[]; hasMore: boolean }>(
    `/cloud-clippings/import/${taskId}/pending-items${queryString ? `?${queryString}` : ''}`,
    { method: 'GET' }
  );
}

/**
 * 删除导入任务
 * DELETE /api/cloud-clippings/import/:taskId
 */
export async function deleteImportTask(taskId: string) {
  return apiFetch<{ success: boolean; message: string }>(
    `/cloud-clippings/import/${taskId}`,
    { method: 'DELETE' }
  );
}

/**
 * 创建云端导入 API 客户端实例
 */
export function createCloudImportApiClient() {
  return {
    createTask: createImportTask,
    startTask: startImportTask,
    pauseTask: pauseImportTask,
    resumeTask: resumeImportTask,
    cancelTask: cancelImportTask,
    getTaskStatus: getImportTaskStatus,
    batchImportItems,
    uploadMedia: uploadImportMedia,
    getTasks: getImportTasks,
    getPendingItems: getPendingImportItems,
    deleteTask: deleteImportTask,
  };
}
